import { AxiosResponse } from 'axios';
import { runInAction } from 'mobx';

import { Optional, ResultKind } from 'services/base/types';
import { createRequestString, getResponseBytes, http, isHttpError } from 'services/base/http';
import { Pangolin } from 'services/accounts/types';

import {
  IPangolinDomain,
  UpdateUpgradeStatus,
  UpdateUpgrade,
  UpdateRelease,
  UpdateHistoryType,
  UpdateHistoryRef,
} from './types';

import {
  UpdateUpgradeStatusMap as UpdateUpgradeStatusMapProto,
  UpdateUpgradeStatus as UpdateUpgradeStatusProto,
  UpdateUpgradeObject as UpdateUpgradeProto,

  CreateUpdateUpgradeRequest,
  CreateUpdateUpgradeResponse,
  GetUpdateUpgradeResponse,
} from './protos/update_pb';

export type CreateUpdateUpgradeResult =
  | { kind: ResultKind.OK; upgrade: UpdateUpgrade }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number }
  | { kind: ResultKind.USER_ERROR; status?: number };

export type CancelUpdateUpgradeResult =
  | { kind: ResultKind.OK }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number }
  | { kind: ResultKind.USER_ERROR; status?: number };

export class UpdateUpgradeService {
  static async fetchActive(pangolin: Pangolin, domain: IPangolinDomain, save = true): Promise<Optional<UpdateUpgrade>> {
    try {
      const httpResp = await http.get(`/pangolin/${domain.id}/update/upgrade`);
      return runInAction(() => {
        const remoteUpgrade = parseGetUpdateUpgradeResponse(httpResp);

        if (save) {
          if (remoteUpgrade) {
            const localUpgrade = domain.updateUpgrades.get(remoteUpgrade.id);
            if (localUpgrade) { Object.assign(localUpgrade, remoteUpgrade); }
            else { domain.updateUpgrades.set(remoteUpgrade.id, remoteUpgrade); }
            pangolin.updateActiveUpgradeID = remoteUpgrade.id;
          } else {
            pangolin.updateActiveUpgradeID = undefined;
          }
        }

        return remoteUpgrade;
      });
    } catch (error) {
      const status = isHttpError(error) ? error.response?.status : undefined;
      if (status === 404) return undefined;
      throw error;
    }
  }

  static async dispatch(domain: IPangolinDomain, pangolin: Pangolin, release: UpdateRelease, password: string): Promise<CreateUpdateUpgradeResult> {
    const request = createCreateUpdateUpgradeRequest(release, password);
    try {
      const httpResp = await http.post(`/pangolin/${domain.id}/update/upgrade`, createRequestString(request));
      const remoteUpgrade = parseCreateUpdateUpgradeResponse(httpResp);
      return runInAction(() => {
        if (remoteUpgrade != null) {
          const remoteHistory = historyFromUpgrade(remoteUpgrade);
          domain.updateUpgrades.set(remoteUpgrade.id, remoteUpgrade);
          domain.updateHistory.setAt(remoteUpgrade.id, remoteHistory, 0);
          pangolin.updateActiveUpgradeID = remoteUpgrade.id;
          return { kind: ResultKind.OK, upgrade: remoteUpgrade };
        }

        return { kind: ResultKind.SYSTEM_ERROR, status: 500 };
      });
    } catch (error) {
      const status = isHttpError(error) ? error.response?.status : undefined;
      const kind = status && status < 500 ? ResultKind.USER_ERROR : ResultKind.SYSTEM_ERROR;
      return { kind, status };
    }
  }

  static async cancel(domain: IPangolinDomain, pangolin: Pangolin): Promise<CancelUpdateUpgradeResult> {
    try {
      await http.delete(`/pangolin/${domain.id}/update/upgrade`);
      return runInAction(() => {
        if (pangolin.updateActiveUpgradeID != null) {
          const localUpgrade = domain.updateUpgrades.get(pangolin.updateActiveUpgradeID);
          if (localUpgrade != null) {
            localUpgrade.terminal_status = localUpgrade.status;
            localUpgrade.status = UpdateUpgradeStatus.CANCELLED;
          }

          pangolin.updateActiveUpgradeID = undefined;
        }

        return { kind: ResultKind.OK };
      });
    } catch (error) {
      const status = isHttpError(error) ? error.response?.status : undefined;
      const kind = status && status < 500 ? ResultKind.USER_ERROR : ResultKind.SYSTEM_ERROR;
      return { kind, status };
    }
  }
}

function historyFromUpgrade(upgrade: UpdateUpgrade): UpdateHistoryRef {
  return { type: UpdateHistoryType.UPGRADE, upgrade_id: upgrade.id };
}

function parseGetUpdateUpgradeResponse(httpResp: AxiosResponse): Optional<UpdateUpgrade> {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = GetUpdateUpgradeResponse.deserializeBinary(rawBytes);
  const itemProto = rootProto.getUpgrade();
  return upgradeFromProto(itemProto);
}

function parseCreateUpdateUpgradeResponse(httpResp: AxiosResponse): Optional<UpdateUpgrade> {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = CreateUpdateUpgradeResponse.deserializeBinary(rawBytes);
  const itemProto = rootProto.getUpgrade();
  return upgradeFromProto(itemProto);
}

type UpdateUpgradeStatusValueProto = UpdateUpgradeStatusMapProto[keyof UpdateUpgradeStatusMapProto];
function updateUpgradeStatusFromProto(proto: Optional<UpdateUpgradeStatusValueProto>): Optional<UpdateUpgradeStatus> {
  switch (proto) {
    case UpdateUpgradeStatusProto.UPDATE_UPGRADE_STATUS_PENDING:
      return UpdateUpgradeStatus.PENDING;
    case UpdateUpgradeStatusProto.UPDATE_UPGRADE_STATUS_DOWNLOAD:
      return UpdateUpgradeStatus.DOWNLOAD;
    case UpdateUpgradeStatusProto.UPDATE_UPGRADE_STATUS_INSTALL:
      return UpdateUpgradeStatus.INSTALL;
    case UpdateUpgradeStatusProto.UPDATE_UPGRADE_STATUS_REBOOT:
      return UpdateUpgradeStatus.REBOOT;
    case UpdateUpgradeStatusProto.UPDATE_UPGRADE_STATUS_LAUNCH:
      return UpdateUpgradeStatus.LAUNCH;
    case UpdateUpgradeStatusProto.UPDATE_UPGRADE_STATUS_BACKUP:
      return UpdateUpgradeStatus.BACKUP;
    case UpdateUpgradeStatusProto.UPDATE_UPGRADE_STATUS_COMPLETED:
      return UpdateUpgradeStatus.COMPLETED;
    case UpdateUpgradeStatusProto.UPDATE_UPGRADE_STATUS_CANCELLED:
      return UpdateUpgradeStatus.CANCELLED;
    case UpdateUpgradeStatusProto.UPDATE_UPGRADE_STATUS_SKIPPED:
      return UpdateUpgradeStatus.SKIPPED;
    case UpdateUpgradeStatusProto.UPDATE_UPGRADE_STATUS_FAILED:
      return UpdateUpgradeStatus.FAILED;
    default:
      return undefined;
  }
}

export function upgradeFromProto(proto: Optional<UpdateUpgradeProto>): Optional<UpdateUpgrade> {
  if (proto == null) return undefined;

  const id = proto.getId();
  const status = updateUpgradeStatusFromProto(proto.getStatus());
  const source_release_id = proto.getSourceReleaseId();
  const target_release_id = proto.getTargetReleaseId();
  const created_at = proto.getCreatedAt();
  const updated_at = proto.getUpdatedAt();
  const terminal_status = (
    !proto.hasTerminalStatus() ? undefined :
    updateUpgradeStatusFromProto(proto.getTerminalStatus())
  );

  if (id === undefined) return undefined;
  if (status === undefined) return undefined;
  if (target_release_id === undefined) return undefined;
  if (created_at === undefined) return undefined;
  if (updated_at === undefined) return undefined;

  return {
    id, status,
    source_release_id,
    target_release_id,
    created_at: created_at.toDate(),
    updated_at: updated_at.toDate(),
    terminal_status,
  };
}

function createCreateUpdateUpgradeRequest(release: UpdateRelease, password: string): CreateUpdateUpgradeRequest {
  const proto = new CreateUpdateUpgradeRequest();
  proto.setReleaseId(release.id);
  proto.setPassword(password);
  return proto;
}
